export type UserRole = 'STUDENT' | 'LECTURER' | 'SUPER_ADMIN'

export type AuthUser = {
  id: number
  username: string
  email: string
  first_name: string
  last_name: string
  role: UserRole
  is_staff: boolean
}

export type Course = {
  id: number
  code: string
  title: string
  credits: number
  department: number
}

export type Enrollment = {
  id: number
  student: number
  student_name: string
  course: number
  course_details: Course
  semester: number | null
  semester_name: string | null
  enrolled_at: string
}

export type CourseAllocation = {
  id: number
  course: number
  course_details: Course
  lecturer: number
  lecturer_name: string
  semester: number
  semester_name: string
}

export type AttendanceSession = {
  id: number
  course_allocation: number
  course_code: string
  course_title: string
  lecturer_name: string
  session_date: string
  start_time: string
  end_time: string
  qr_code_token: string | null
  created_at: string
}

export type AttendanceRecord = {
  id: number
  session: number
  course_code: string
  student: number
  status: 'PRESENT' | 'ABSENT' | 'EXCUSED'
  timestamp: string
}

export type LearningMaterial = {
  id: number
  module: number
  module_title: string
  course_code: string
  course_title: string
  title: string
  file: string | null
  external_link: string | null
  uploaded_at: string
}

export type Module = {
  id: number
  course_allocation: number
  course_code: string
  course_title: string
  title: string
  description: string | null
  order: number
  materials: LearningMaterial[]
}

export type Assignment = {
  id: number
  course_allocation: number
  course_code: string
  course_title: string
  title: string
  instructions: string
  due_date: string
  max_marks: string
}

export type AssignmentSubmission = {
  id: number
  assignment: number
  assignment_title: string
  student: number
  submitted_file: string | null
  marks_obtained: string | null
  feedback: string | null
  submitted_at: string
}

export type Assessment = {
  id: number
  course_allocation: number
  course_code: string
  course_title: string
  title: string
  assessment_type: string
  max_marks: string
  weight_percentage: string
  date_held: string | null
}

export type Notification = {
  id: number
  title: string
  message: string
  is_read: boolean
  created_at: string
}

/** Empty in local Vite (proxy); set to https://api.academics.wesacco.com in production builds. */
const API_ORIGIN = (import.meta.env.VITE_API_ORIGIN as string | undefined)?.replace(/\/$/, '') || ''

const ACCESS_KEY = 'sa_access'
const REFRESH_KEY = 'sa_refresh'

function apiUrl(path: string): string {
  const p = path.startsWith('/') ? path : `/api/v1/${path}`
  return `${API_ORIGIN}${p}`
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY)
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY)
}

export function setTokens(access: string, refresh: string): void {
  localStorage.setItem(ACCESS_KEY, access)
  localStorage.setItem(REFRESH_KEY, refresh)
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
}

async function refreshAccess(): Promise<string | null> {
  const refresh = getRefreshToken()
  if (!refresh) return null
  const res = await fetch(apiUrl('/api/v1/auth/token/refresh/'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  })
  if (!res.ok) {
    clearTokens()
    return null
  }
  const data = (await res.json()) as { access: string }
  localStorage.setItem(ACCESS_KEY, data.access)
  return data.access
}

export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  const isForm = typeof FormData !== 'undefined' && init.body instanceof FormData
  if (init.body && !isForm && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  const token = getAccessToken()
  if (token) headers.set('Authorization', `Bearer ${token}`)

  let res = await fetch(apiUrl(path), {
    ...init,
    headers,
  })

  if (res.status === 401 && getRefreshToken()) {
    const next = await refreshAccess()
    if (next) {
      headers.set('Authorization', `Bearer ${next}`)
      res = await fetch(apiUrl(path), {
        ...init,
        headers,
      })
    }
  }

  if (!res.ok) {
    const text = await res.text()
    throw new Error(text || `Request failed (${res.status})`)
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export async function login(username: string, password: string): Promise<AuthUser> {
  const tokens = await api<{ access: string; refresh: string }>('/api/v1/auth/token/', {
    method: 'POST',
    body: JSON.stringify({ username, password }),
  })
  setTokens(tokens.access, tokens.refresh)
  return api<AuthUser>('/api/v1/auth/me/')
}

export async function fetchMe(): Promise<AuthUser> {
  return api<AuthUser>('/api/v1/auth/me/')
}

export const listEnrollments = () => api<Enrollment[]>('/api/v1/enrollments/')
export const listAllocations = () => api<CourseAllocation[]>('/api/v1/course-allocations/')
export const listAttendanceSessions = () => api<AttendanceSession[]>('/api/v1/attendance-sessions/')
export const listAttendanceRecords = () => api<AttendanceRecord[]>('/api/v1/attendance-records/')
export const listMaterials = () => api<LearningMaterial[]>('/api/v1/learning-materials/')
export const listModules = () => api<Module[]>('/api/v1/modules/')
export const listAssignments = () => api<Assignment[]>('/api/v1/assignments/')
export const listSubmissions = () => api<AssignmentSubmission[]>('/api/v1/assignment-submissions/')
export const listAssessments = () => api<Assessment[]>('/api/v1/assessments/')
export const listNotifications = () => api<Notification[]>('/api/v1/notifications/')

export function signAttendance(sessionId: number) {
  return api<AttendanceRecord>('/api/v1/attendance-records/', {
    method: 'POST',
    body: JSON.stringify({ session: sessionId }),
  })
}

export function openAttendanceSession(payload: {
  course_allocation: number
  session_date: string
  start_time: string
  end_time: string
  qr_code_token?: string
}) {
  return api<AttendanceSession>('/api/v1/attendance-sessions/', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function createModule(payload: {
  course_allocation: number
  title: string
  description?: string
  order?: number
}) {
  return api<Module>('/api/v1/modules/', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function createMaterial(form: FormData) {
  return api<LearningMaterial>('/api/v1/learning-materials/', {
    method: 'POST',
    body: form,
  })
}

export function createAssessment(payload: {
  course_allocation: number
  title: string
  assessment_type: string
  max_marks: string
  weight_percentage: string
  date_held?: string | null
}) {
  return api<Assessment>('/api/v1/assessments/', {
    method: 'POST',
    body: JSON.stringify(payload),
  })
}

export function submitAssignment(assignmentId: number, file?: File) {
  const form = new FormData()
  form.append('assignment', String(assignmentId))
  if (file) form.append('submitted_file', file)
  return api<AssignmentSubmission>('/api/v1/assignment-submissions/', {
    method: 'POST',
    body: form,
  })
}

export function isSessionActive(session: AttendanceSession, now = new Date()): boolean {
  const date = session.session_date
  const start = new Date(`${date}T${session.start_time}`)
  const end = new Date(`${date}T${session.end_time}`)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
  return now >= start && now <= end
}

export function formatDue(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}
